/*
 *  Copyright (c) 2018-present, Evgeny Nadymov
 *
 * This source code is licensed under the GPL v.3.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Slider from '@material-ui/core/Slider';
import FullScreen from '../../Assets/Icons/FullScreen';
import MdSubtitles from '../../Assets/Icons/MdSubtitles';
import Close from '../../Assets/Icons/Close';
import PlayIcon from '../../Assets/Icons/PlayArrow';
import PauseIcon from '../../Assets/Icons/Pause';
import Hint from './Hint';
import Progress from './Progress';
import { clamp, getDurationString, isIOS } from '../../Utils/Common';
import MovieInfo from '../../Utils/MovieInfo';
import MessageStore from '../../Stores/MessageStore';
import {
    PLAYER_LOOP_MAX_DURATION,
} from '../../Constants';
import FileStore from '../../Stores/FileStore';
import PlayerStore from '../../Stores/PlayerStore';
import TdLibController from '../../Controllers/TdLibController';
import './Player.css';
import { MdOutlineSubtitles, MdOutlineSubtitlesOff, MdSettings } from 'react-icons/md';
import { BsJournalBookmark } from 'react-icons/bs'
import SubtitleUtil from '../../Utils/SubtitleUtil';
import PlayerMethods from './Player.Methods'
import { makePlayerRef } from './PlayerRef';

const isSafariBrowser = () => navigator.userAgent.indexOf('Safari') > -1 && navigator.userAgent.indexOf('Chrome') <= -1;
// console.log('isSafariBrowser', isSafariBrowser());

class Player extends PlayerMethods {
    constructor(props) {
        super(props);

        this.rootRef = React.createRef();
        this.contentRef = React.createRef();
        this.videoRef = React.createRef();

        const { currentTime, duration } = this.getCurrentTime();

        this.state = {
            noPoster: currentTime > 0 && duration > 0,
            duration,
            currentTime,
            volume: PlayerStore.volume,
            play: true,
            dragging: false,
            buffered: null,
            waiting: true,
            hidden: false,
            // enableSubtitle: !!localStorage.getItem('enableSubtitle'),
            enableSubtitle: true,
            videoBookmarkParts: [],
        };
    }

    

    componentDidMount() {
        makePlayerRef(this);
        PlayerStore.on('clientUpdateMediaShortcut', this.onClientUpdateMediaShortcut);
        PlayerStore.on('clientUpdateChangeSubtitle', this.onClientUpdateChangeSubtitle);

        const video = this.videoRef.current;
        if (!video) return;

        const { volume } = this.state;

        video.volume = volume;
        const { src } = this.props;
    }

    componentWillUnmount() {
        PlayerStore.off('clientUpdateMediaShortcut', this.onClientUpdateMediaShortcut);
        PlayerStore.off('clientUpdateChangeSubtitle', this.onClientUpdateChangeSubtitle);
    }

    onClientUpdateChangeSubtitle = update => {
        const { url } = update.data || {};
        if (url === null) {
            this.removeSub();
            return;
        }
        if (!url) return;
        this.removeSub();
        const video = this.videoRef.current;
        if (!video) return;
        const track = document.createElement("track");
        track.kind = "captions";
        track.label = "Custom";
        track.srclang = "en";
        track.setAttribute('default', true);
        track.addEventListener("load", function () {
            this.mode = "showing";
            video.textTracks[0].mode = "showing"; // thanks Firefox 
        });
        // track.addEventListener('cuechange', function (event) {
        //     if (window.innerWidth > 480) return;
        //     let cues = track.activeCues;
        //     if (!cues) return;
        //     for (let i = 0; i < cues.length; i++) {
        //       cues[i].line = '70%'
        //     }
        // });
        track.src = url;
        video.appendChild(track);
    }

    

    

    

    handleEnded = event => {
        const { onEnded } = this.props;

        TdLibController.clientUpdate({ '@type': 'clientUpdateMediaViewerEnded' });
        onEnded && onEnded(event);

        const video = this.videoRef.current;
        this.setCurrentTime({ currentTime: 0, duration: video.duration });
        this.saveProgress(99);
    };

    handleTimeUpdate = () => {
        const video = this.videoRef.current;
        if (!video) return;

        const { fileId } = this.props;
        const { currentTime, duration, volume, buffered } = video;

        this.setState({
            duration,
            currentTime,
            volume,
            buffered
        });

        TdLibController.clientUpdate({
            '@type': 'clientUpdateMediaViewerTimeUpdate',
            fileId,
            currentTime,
            duration,
            volume,
            buffered
        });

        this.setCurrentTime({ currentTime, duration });
    };

    

    handleLoadedMetadata = () => {
        const video = this.videoRef.current;
        if (!video) return;

        const { currentTime, enableSubtitle } = this.state;
        const { duration, volume, buffered } = video;

        this.setState({
            duration,
            volume,
            waiting: true,
            buffered
        }, () => {
            if (!!currentTime) {
                video.currentTime = currentTime;
            }
            const { messageId, chatId } = this.props;
            const message = MessageStore.get(chatId, messageId);
            const filename = message.content.video.file_name;
            const imdb = MovieInfo.imdbFromMessage(message);
            const currentPercentage = MovieInfo.getRecent(imdb, filename);
            if (currentPercentage) {
                const newCurrentTime = currentPercentage * duration / 100;
                video.currentTime = newCurrentTime;
            }
        });
        this.updateCurrentPlaying();
        // if (enableSubtitle) this.addDemoSub(video);
        if (enableSubtitle) this.searchSubtitles();
        this.getVideoBookmarParts();
    };

    getVideoBookmarParts = () => {
        const { messageId, chatId } = this.props;
        const message = MessageStore.get(chatId, messageId);
        const parts = MovieInfo.parsePartFromMessage(message);
        console.log('getVideoBookmarParts', parts);
        if (parts.length > 0) {
            this.setState({
                videoBookmarkParts: parts
            })
        }
    }

    updateCurrentPlaying = (isPlaying = true) => {
        SubtitleUtil.resetSubPosition();
        const { messageId, chatId } = this.props;
        const message = MessageStore.get(chatId, messageId);
        const filename = message.content.video.file_name;
        let imdb = MovieInfo.imdbFromMessage(message);
        PlayerStore.onClientUpdate({
            '@type': 'clientUpdateCurrentPlaying',
            data: !isPlaying ? null : {
                title: filename,
                imdb,
            },
        });
        if (!isPlaying) {
            this.setState({
                videoBookmarkParts: []
            })
        }
    }

    searchSubtitles = async () => {
        // if (!video) return;
        const { messageId, chatId } = this.props;
        const message = MessageStore.get(chatId, messageId);
        // console.log('searchSubtitles message', message);
        const filename = message.content.video.file_name;
        const messageText = message.content.caption.text;
        // console.log('filename', filename);
        // console.log('messageText', messageText);
        const seasonInfo = MovieInfo.parseSeasonAndEpisodeFromTitle(messageText, filename);
        console.log('seasonInfo', seasonInfo, messageText, filename);
        let imdb = MovieInfo.imdbFromMessage(message);
        const searchObj = {
            imdb,
            title: filename,
        };
        if (seasonInfo && seasonInfo.season) {
            searchObj.season = seasonInfo.season
            searchObj.episode = seasonInfo.episode
        }
        const subs = await SubtitleUtil.searchSubtitle(searchObj);
        // console.log('sub', subs);
        if (subs.length > 0) {
            PlayerStore.onClientUpdate({
                '@type': 'clientUpdateSubtitleSearchResults',
                data: {
                    ...searchObj,
                    subtitles: subs,
                },
            });
        }
    }

    removeSub = (video = this.videoRef.current) => {
        if (!video) return;
        const tracks = video.querySelectorAll(':scope > track');
        tracks.forEach(el => el.remove());
    }

    handleToggleBookmarkPage = () => {
        PlayerStore.onClientUpdate({
            '@type': 'clientUpdateBookmarkPage',
            data: {
                show: true,
                parts: this.state.videoBookmarkParts,
            },
        })
    }

    handleToggleSubtitlePage = () => {
        PlayerStore.onClientUpdate({
            '@type': 'clientUpdateSubtitlePage',
            data: {
                show: true,
            },
        })
    }

    handleToggleSubtitle = () => {
        // this.handleToggleSubtitlePage();
        const { enableSubtitle } = this.state;
        localStorage.setItem('enableSubtitle', enableSubtitle ? '0' : '1');
        this.setState({
            enableSubtitle: !enableSubtitle,
        }, () => {
            if (!enableSubtitle) {
                // this.addDemoSub();
            }
            else this.removeSub();
        });
    }

    handleProgress = () => {
        const video = this.videoRef.current;
        if (!video) return;

        const { fileId } = this.props;

        const { buffered } = video;

        this.setState({
            buffered
        });

        TdLibController.clientUpdate({
            '@type': 'clientUpdateMediaViewerProgress',
            fileId,
            buffered
        });

        const { videoBookmarkParts } = this.state;
        let foundBookmark = false;
        for (let i=0; i<videoBookmarkParts.length; i++) {
            const { start, end, name, startText, endText } = videoBookmarkParts[i];
            if (video.currentTime >= start && video.currentTime < end) {
                foundBookmark = true;
                this.setState({
                    currentBookmarkPart: {
                        start, end, name, startText, endText
                    }
                })
            }
        }
        // console.log('foundBookmark', foundBookmark);
        if (!foundBookmark) {
            this.setState({ currentBookmarkPart: undefined });
        }
    };

    handleClose = () => {
        this.saveProgress();
        const event = new KeyboardEvent("keydown", {
            'key': 'Escape'
        });
        document.dispatchEvent(event);
        this.updateCurrentPlaying(false);
        PlayerStore.videoBookmarkParts = [];
    };

    saveProgress = (forcePercentage) => {
        const { messageId, chatId } = this.props;
        const message = MessageStore.get(chatId, messageId);
        const filename = message.content.video.file_name;
        const imdb = MovieInfo.imdbFromMessage(message);
        const { currentTime, duration } = this.getCurrentTime();
        const progress = duration === 0 ? 0 : (currentTime / duration) * 100;
        MovieInfo.saveRecent(imdb, filename, forcePercentage || progress)
    }

    getCurrentTime = () => {
        const { fileId } = this.props;

        const file = FileStore.get(fileId);
        if (!file) return { currentTime: 0, duration: 0 };

        const { remote } = file;
        if (!remote) return { currentTime: 0, duration: 0 };

        const { unique_id } = remote;
        if (!unique_id) return { currentTime: 0, duration: 0 };

        return PlayerStore.getCurrentTime(unique_id);
    };

    setCurrentTime = currentTime => {
        const { fileId } = this.props;

        const file = FileStore.get(fileId);
        if (!file) return;

        const { remote } = file;
        if (!remote) return;

        const { unique_id } = remote;
        if (!unique_id) return;

        if (!currentTime) {
            PlayerStore.clearCurrentTime(unique_id);
        } else {
            PlayerStore.setCurrentTime(unique_id, currentTime);
        }
    };

    getFileName = () => {
        const { messageId, chatId } = this.props;
        const message = MessageStore.get(chatId, messageId);
        const filename = message.content.video.file_name;
        return filename;
    }

    render() {
        const { children, src, className, style, width, height, poster, fileId } = this.props;
        const { waiting, volume, duration, currentTime, play, dragging, draggingTime, buffered, hidden, noPoster, enableSubtitle, currentBookmarkPart, videoBookmarkParts } = this.state;

        const time = dragging ? draggingTime : currentTime;
        const value = duration > 0 ? time / duration : 0;
        const bufferedTime = Player.getBufferedTime(time, buffered);
        const bufferedValue = duration > 0 ? bufferedTime / duration : 0;

        const timeString = getDurationString(Math.floor(time) || 0);
        const durationString = getDurationString(Math.floor(duration) || 0);
        const rootStyle = {
            ...style,
            width,
            height
        }

        const fullscreenEnabled = document.fullscreenEnabled || document.mozFullScreenEnabled || document.webkitFullscreenEnabled;
        const pictureInPictureEnabled = true;//document.pictureInPictureEnabled || document.mozPictureInPictureEnabled || document.webkitPictureInPictureEnabled;

        return (
            <div
                ref={this.rootRef}
                className={classNames('player', className)}
                onMouseDown={this.handleMouseDownRoot}
                onClick={this.handleClickRoot}
                onDoubleClick={this.handleDoubleClick}
                style={rootStyle}>
                <div ref={this.contentRef} className={classNames('player-content', { 'player-content-hidden': hidden })} onMouseMove={this.handleMouseOver}>
                    <video
                        id='pyraplayer-player'
                        className='player-video'
                        ref={this.videoRef}
                        autoPlay={false}
                        controls={false}
                        playsInline={true}
                        src={src}
                        poster={noPoster ? null : poster}
                        loop={duration <= PLAYER_LOOP_MAX_DURATION}
                        onLoadedMetadata={this.handleLoadedMetadata}
                        onLoadedData={this.handleLoadedData}
                        onCanPlay={this.handleCanPlay}
                        onPlay={this.handlePlay}
                        onPause={this.handlePause}
                        onEnded={this.handleEnded}
                        onTimeUpdate={this.handleTimeUpdate}
                        onVolumeChange={this.handleVolumeChange}
                        onProgress={this.handleProgress}
                        onWaiting={this.handleWaiting}
                        onKeyDown={this.handleVideoKeyDown}
                    >
                        {children}
                    </video>
                    <Hint fileId={fileId} />
                    <div
                        className={classNames('player-panel', { 'player-panel-hidden': hidden })}
                        onClick={e => e.stopPropagation()}
                        onMouseDown={e => e.stopPropagation()}
                        onDoubleClick={this.handlePanelDoubleClick}
                        onMouseEnter={this.handlePanelEnter}
                        onMouseLeave={this.handlePanelLeave}>
                        <div className='player-slider'>
                            <span className='player-slider-buffer-track' style={{ width: bufferedValue * 100 + '%' }} />
                            <Slider
                                value={value}
                                min={0}
                                max={1}
                                step={0.001}
                                classes={{
                                    root: 'player-slider-root',
                                    rail: 'player-slider-rail',
                                    track: 'player-slider-track',
                                    thumb: 'player-slider-thumb',
                                    active: 'player-slider-active'
                                }}
                                onChange={this.handleChange}
                                onChangeCommitted={this.handleChangeCommitted}
                                onMouseDown={this.handleMouseDown}
                            />
                        </div>
                        <div style={{ position: 'fixed', left: 0, right: 0, bottom: 'auto', top: 0, textAlign: 'center', padding: 20, backgroundColor: 'rgba(0,0,0,0.5)' }}>
                            {this.getFileName()}
                        </div>
                        <div className='player-controls'>
                            <button className='player-button' onClick={this.handleClick}>
                                {play ? <PauseIcon /> : <PlayIcon />}
                            </button>
                            <div className='player-time'>
                                {`${timeString} / ${durationString}`}
                            </div>
                            {!isIOS() && (
                                <div className='player-volume'>
                                    <Slider
                                        value={volume}
                                        min={0}
                                        max={1}
                                        step={0.001}
                                        classes={{
                                            root: 'player-volume-root',
                                            rail: 'player-volume-rail',
                                            track: 'player-volume-track',
                                            thumb: 'player-volume-thumb',
                                            active: 'player-volume-active'
                                        }}
                                        onChange={this.handleVolumeSliderChange}
                                        onChangeCommitted={this.handleVolumeSliderChangeCommitted}
                                    />
                                </div>
                            )}
                            

                            {videoBookmarkParts && videoBookmarkParts.length > 0 && (
                                <button className='player-button' onClick={this.handleToggleBookmarkPage}>
                                    <BsJournalBookmark fontSize={{ color: 'white' }} />
                                </button>
                            )}
                            <button className='player-button' onClick={this.handleToggleSubtitlePage}>
                                {enableSubtitle ? (isIOS() ? <MdSubtitles /> : <MdOutlineSubtitles fontSize={{ color: 'white' }} />) : <MdOutlineSubtitlesOff fontSize={{ color: 'white' }} />}
                            </button>
                            <button className='player-button' onClick={this.handleMute}>
                                {Player.getVolumeIcon(volume)}
                            </button>
                            {/* <button className='player-button' disabled={!pictureInPictureEnabled} onClick={this.handlePictureInPicture}>
                                <PictureInPictureIcon />
                            </button>
                            <button className='player-button' disabled={!fullscreenEnabled} onClick={this.handleFullScreen}>
                                <FullScreen />
                            </button> */}
                            {!isSafariBrowser() && (
                                <button className='player-button' disabled={!fullscreenEnabled} onClick={this.handleFullScreen}>
                                    <FullScreen />
                                </button>
                            )}
                            {isIOS() && (
                                <button className='player-button' onClick={this.handleFullScreenIOS}>
                                    <FullScreen />
                                </button>
                            )}
                            <button className='player-button' onClick={this.handleClose}>
                                <Close />
                            </button>
                        </div>
                    </div>
                    <Progress waiting={waiting} />
                </div>
                {!!currentBookmarkPart && currentBookmarkPart.name && (
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            this.handleSeek(currentBookmarkPart.end);
                        }}
                        style={{ cursor: 'pointer', position: 'absolute', bottom: 60, right: 20, padding: '10px 20px', border: '1px solid white' }}
                    >
                        {currentBookmarkPart.name}
                    </div>
                )}
            </div>
        );
    }
}

Player.propTypes = {
    fileId: PropTypes.number
};

//export default React.forwardRef((props, ref) => (<Player forwardedRef={ref} {...props}/>));

export default Player;
